import { forwardRef, useEffect, useImperativeHandle, useRef, type FormEvent, type KeyboardEvent } from 'react'
import { ArrowUp, Check, Compass, Loader2, Wrench, X } from '@/ui/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/components/primitives/dropdown-menu'
import type { RangerTool } from '@/shared/api/ranger'
import { cn } from '@/shared/utils'

const TEXTAREA_MAX_HEIGHT_PX = 120

const TOOL_LABELS: Record<RangerTool, string> = {
  build_trip: 'Build a trip',
}

export interface RangerToolControls {
  activeTool: RangerTool | null
  /** Build a trip has created its trip, so the chat has moved on to inviting people. */
  tripBuilt: boolean
  onStart: (tool: RangerTool) => void
  onStop: () => void
}

interface RangerComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  loading: boolean
  placeholder: string
  tools?: RangerToolControls
}

export const RangerComposer = forwardRef<HTMLTextAreaElement, RangerComposerProps>(function RangerComposer(
  { value, onChange, onSubmit, loading, placeholder, tools },
  forwardedRef
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(forwardedRef, () => textareaRef.current as HTMLTextAreaElement)

  // Grow with the content, capped so it can't swallow the panel
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`
  }, [value])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit()
    }
  }

  const activeTool = tools?.activeTool ?? null

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full rounded-xl border border-earth-200 bg-background p-2.5 shadow-sm transition-colors focus-within:border-adventure-400"
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label="Message Ranger"
        disabled={loading}
        rows={2}
        maxLength={5000}
        className="block max-h-[120px] w-full resize-none bg-transparent px-1 text-sm text-foreground placeholder:text-earth-600 focus:outline-none disabled:opacity-60"
      />
      <div className="mt-2 flex items-center gap-1.5">
        {tools && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-earth-200 px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-earth-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500"
              >
                <Wrench className="h-3.5 w-3.5" aria-hidden />
                Tools
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-64">
              <DropdownMenuItem
                onClick={() => (activeTool === 'build_trip' ? tools.onStop() : tools.onStart('build_trip'))}
                className="items-start gap-2.5 py-2"
              >
                <Compass className="mt-0.5 h-4 w-4 shrink-0 text-adventure-600" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{TOOL_LABELS.build_trip}</span>
                  <span className="block text-xs text-earth-600">
                    Tell Ranger the basics. It sets up the trip and invites your crew.
                  </span>
                </span>
                {activeTool === 'build_trip' && <Check className="mt-0.5 h-4 w-4 shrink-0" aria-label="On" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {tools && activeTool && (
          <span className="inline-flex h-8 min-w-0 items-center gap-1 rounded-full bg-adventure-100 pl-2.5 pr-1 text-xs font-medium text-adventure-800">
            <span className="truncate">{TOOL_LABELS[activeTool]}</span>
            <button
              type="button"
              onClick={tools.onStop}
              aria-label={`Turn off ${TOOL_LABELS[activeTool]}`}
              className="rounded-full p-1 transition-colors hover:bg-adventure-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        )}
        <button
          type="submit"
          disabled={!value.trim() || loading}
          aria-label="Send message"
          className={cn(
            'ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'bg-adventure-600 text-white hover:bg-adventure-700',
            'disabled:cursor-not-allowed disabled:bg-earth-200 disabled:text-earth-600'
          )}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </div>
    </form>
  )
})
